import React, { useMemo, useState } from 'react';
import TabNavItem from '../../../../components/tabs/TabNavItem';
import TabContent from '../../../../components/tabs/TabContent';
import GTable from '../../../../components/elements/GTable';
import { Edit, Plus, PlusCircle, Shield, Trash2 } from 'lucide-react';
import Button from '../../../../components/form-control/Button';
import useModal from '../../../../hooks/modalHook';
import useInsuranceConfiguration from '../../../../hooks/insuranceConfigurationHook';
import { Navigate, useNavigate } from 'react-router-dom';
import useRoute from '../../../../hooks/routesHook';
import {
  getAllMotorInsuranceConfigurations,
  getAllNoClaimDiscount,
  getAllRiskTypes,
  getListOfPortfolios,
  getVehicleBodyTypesForInsuranceConfiguration
} from '../../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import { IInsuranceType } from './AddInsuranceTypePage';

interface ITabsContent {
  id: string;
  label: string;
  onClick: () => void;
  tableType: string;
  data: any[];
}

interface ITabs {
  id: string;
  title: string;
}

interface Props {
  handleActiveTab: (tab: string) => void;
}
const InsuranceConfiguration = ({ handleActiveTab }: Props) => {
  const [activeTab, setActiveTab] = useState<any>('Risk type');
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const {
    AddNoClaimDiscount,
    AddCoverageType,
    InsuranceConfigurationModal,
    setSelectedTabItem,
    riskTypes,
    setLoading,
    loading,
    columnFirstTable,
    columnSecondTable,
    columnThirdTable,
    columnFourthTable,
    columnFithTable
  } = useInsuranceConfiguration();
  
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const [allMotorInsuranceConfigurations, setAllMotorInsuranceConfigurations] = useState<
    IInsuranceType[]
  >([]);
 
  const [coverageTypes, setportfolios] = useState([]);
  const [bodyCounts, setBodyCounts] = useState([]);
  const [noClaimDiscount, setNoClaimDiscount] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRiskTypes = useMemo(() => {
    if (!searchQuery) return riskTypes;
    
    const query = searchQuery.toLowerCase();
    return riskTypes?.filter((riskType: any) => {
      return (
        (riskType?.name?.toLowerCase().includes(query))
      );
    });
  }, [riskTypes, searchQuery]);

  useQuery({
    queryKey: ['getMotorInsuranceConfigurations'],
    queryFn: () => getMotorInsuranceConfigurations()
  });

  useQuery({ queryKey: ['getCoverageType'], queryFn: () => getCoverageType() });
  useQuery({ queryKey: ['getAllNoClaimDiscounts'], queryFn: () => getAllNoClaimDiscounts() });
  useQuery({ queryKey: ['getVehicleBodyTypes'], queryFn: () => getVehicleBodyTypes() });

  const getMotorInsuranceConfigurations = async () => {
    getAllMotorInsuranceConfigurations()
      .then((response: any) => {
        setAllMotorInsuranceConfigurations(response);
      })
      .catch((error: Error) => {
        console.log('error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getVehicleBodyTypes = async () => {
    getVehicleBodyTypesForInsuranceConfiguration()
      .then((response: any) => {
        setBodyCounts(response.data);
      })
      .catch((error: Error) => {
        console.log('error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };



  const getCoverageType = async () => {
    getListOfPortfolios()
      .then((response: any) => {
        setportfolios(response);
      })
      .catch((error: Error) => {
        console.log('error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getAllNoClaimDiscounts = async () => {
    getAllNoClaimDiscount()
      .then((response: any) => {
        setNoClaimDiscount(response);
      })
      .catch((error: Error) => {
        console.log('error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const tabItems = [

    {
      id: 'Risk type',
      title: 'Risk type'
    },
    {
      id: 'Insurance type',
      title: 'Insurance type'
    },
    {
      id: 'Body type',
      title: 'Body type'
    },
    {
      id: 'Coverage type',
      title: 'Coverage type'
    },
    {
      id: 'No claim discount',
      title: 'No claim discount'
    }
  ];
  const tabContents = [
    {
      id: 'Insurance type',
      label: 'Add Insurance Type',
      onClick: () =>
        navigate(`${getDashboardBase()}/insurance-configuration/insurance-type?action=create`),
      tableType: 'insurance type',
      data: allMotorInsuranceConfigurations
    },
    {
      id: 'Body type',
      label: 'Add Body Type',
      onClick: () => AddNoClaimDiscount(),
      tableType: 'body type',
      data: bodyCounts
    },
    {
      id: 'Risk type',
      label: 'Add Risk Type',
      onClick: () =>
        navigate(`${getDashboardBase()}/insurance-configuration/insurance-risk-type?action=create`),
      tableType: 'risk type',
      data: filteredRiskTypes
    },
    {
      id: 'Coverage type',
      label: 'Add Coverage type',
      onClick: () => AddCoverageType(),
      tableType: 'Coverage type',
      data: coverageTypes
    },
    {
      id: 'No claim discount',
      label: 'Add Discount Rate',
      onClick: () => AddNoClaimDiscount(),
      tableType: 'claim discount',
      data: noClaimDiscount
    }
  ];

  const handleTabClicked = (id: string) => {
    setActiveTab(id);
    setSelectedTabItem(id);
    handleActiveTab(id);
  };

  //Columns listed based on the tabContents list
  const columns = React.useMemo(() => {
    if (activeTab === 'Insurance type') {
      return [columnFirstTable];
    }

    if (activeTab === 'Body type') {
      return [columnSecondTable];
    }

    if (activeTab === 'Risk type') {
      return [columnThirdTable];
    }

    if (activeTab === 'Coverage type') {
      return [columnFourthTable];
    }

     if (activeTab == 'No claim discount') {
      return [columnFithTable];
    }

    return [];
  }, [activeTab]);

  return (
    <div>
      <div className="flex mt-6 flex-col md:flex-row md:justify-between md:items-center">
        <div className="flex border-b border-gray-100 dark:border-darkBg-border">
          {tabItems.map((item: ITabs, index: number) => (
            <TabNavItem
              key={index}
              id={item.id}
              title={item.title}
              onTabClicked={handleTabClicked}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              classes="text-sm"
            />
          ))}
        </div>
      </div>

      <div>
        {tabContents.map((content: ITabsContent, index: number) => (
          <TabContent key={index} id={content.id} activeTab={activeTab}>
            {index === 2 ? <GTable
              columns={columns}
              data={content.data}
              onSearch={(searchValue) => setSearchQuery(searchValue)}
              searchPlaceholder='Search by name'
              isRowLinked={false}
              tableType={content.tableType}
              loading={loading}
              classes={'max-h-[400px]'}
              stickyHeader
            /> :
            <GTable
              columns={columns}
              data={content.data}
              showSearch={false}
              onSearch={(searchValue) => setSearchQuery(searchValue)}
              isRowLinked={false}
              tableType={content.tableType}
              loading={loading}
              classes={'max-h-[400px]'}
              stickyHeader
            />}
          </TabContent>
        ))}
      </div>
      <ModalComponent />
      <InsuranceConfigurationModal />
    </div>
  );
};

export default InsuranceConfiguration;
